/**
 * <ManagerOnboardingDashboard /> — table of employees in onboarding.
 *
 * Route: /admin/onboarding
 *
 * Filters: All / Stalled (no activity 3+ days) / Almost done (>80%).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { differenceInDays, format } from 'date-fns';
import { Plus, ChevronRight, UserPlus, Settings, FileSignature } from 'lucide-react';
import { db } from '../../../firebase';
import '../Onboarding.css';

const FILTERS = [
  { id: 'all',    label: 'All' },
  { id: 'stalled', label: 'Stalled (3d+)' },
  { id: 'almost', label: 'Almost done' },
];

function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v === 'string') { const d = new Date(v); return isNaN(d) ? null : d; }
  if (v instanceof Date) return v;
  return null;
}

export default function ManagerOnboardingDashboard() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let active = true;
    (async () => {
      const empSnap = await getDocs(
        query(collection(db, 'employees'), where('status', '==', 'onboarding'))
      );
      const enriched = await Promise.all(empSnap.docs.map(async (d) => {
        const emp = { id: d.id, ...d.data() };
        const progSnap = await getDoc(doc(db, 'onboarding_progress', emp.id));
        const prog = progSnap.exists() ? progSnap.data() : null;

        // Find the first pending item (blocking) and last activity.
        const itemsSnap = await getDocs(
          query(collection(db, 'checklist_items'), where('employee_id', '==', emp.id))
        );
        const items = itemsSnap.docs.map((x) => ({ id: x.id, ...x.data() }));
        const pending = items
          .filter((i) => i.status !== 'completed')
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        const lastActivity = items
          .map((i) => toDate(i.completed_at))
          .filter(Boolean)
          .sort((a, b) => b - a)[0] || toDate(emp.onboarding_started_at);

        return {
          ...emp,
          percent: prog?.percent_complete ?? 0,
          blocking: pending[0]?.title || '—',
          lastActivity,
        };
      }));
      if (active) {
        setRows(enriched);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const now = new Date();
    return rows.filter((r) => {
      if (filter === 'stalled') {
        return r.lastActivity && differenceInDays(now, r.lastActivity) >= 3;
      }
      if (filter === 'almost') return (r.percent || 0) > 80;
      return true;
    });
  }, [rows, filter]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', margin: 0 }}>Onboarding</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="onboarding-btn secondary"
            onClick={() => navigate('/admin/policy-templates')}
            title="Manage onboarding templates and policies"
          >
            <Settings size={14} /> Manage templates
          </button>
          <button
            className="onboarding-btn secondary"
            onClick={() => navigate('/admin/onboarding/assign-policies')}
            title="Assign individual policy acknowledgements to any employee"
          >
            <FileSignature size={14} /> Assign policies
          </button>
          <button
            className="onboarding-btn secondary"
            onClick={() => navigate('/admin/onboarding/assign')}
            title="Assign a full onboarding template to an existing employee"
          >
            <UserPlus size={14} /> Assign template
          </button>
          <button className="onboarding-btn" onClick={() => navigate('/admin/onboarding/new')}>
            <Plus size={14} /> New hire
          </button>
        </div>
      </div>
      <p style={{ color: '#555', margin: '6px 0 0', fontSize: '0.85rem' }}>
        Click a row to view that employee's checklist, mark manager tasks complete (I-9 Section 2,
        background check, etc.), and review their uploads. Progress only moves once the employee
        completes their forms or you mark a manager task done.
      </p>

      <div className="onboarding-filter-tabs">
        {FILTERS.map((f) => (
          <button key={f.id} className={filter === f.id ? 'active' : ''} onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner-large" /><p>Loading…</p></div>
      ) : filtered.length === 0 ? (
        <div className="onboarding-empty">No employees match this filter.</div>
      ) : (
        <table className="onboarding-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Hire date</th>
              <th>% complete</th>
              <th>Last activity</th>
              <th>Blocking item</th>
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} onClick={() => navigate(`/admin/onboarding/${r.id}`)}>
                <td>{r.firstName} {r.lastName}</td>
                <td>{r.hire_date || r.hireDate || '—'}</td>
                <td>{r.percent}%</td>
                <td>{r.lastActivity ? format(r.lastActivity, 'MMM d') : '—'}</td>
                <td>{r.blocking}</td>
                <td className="onboarding-table-chevron"><ChevronRight size={16} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
